import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import type { ReactNode } from "react";
import {
  beginStackExitAnimation,
  endStackExitAnimation,
  getNavBackTarget,
  isExitingSettingsSubpage,
  isExitingSettingsToApp,
  markNavDirection,
  markSkipStackEnter,
  hasHistoryBackEntry,
  shouldUseSettingsStackTransition,
} from "./navigationUtils";

const STACK_TRANSITION_MS = 280;

function BackLink({
  backTo,
  className,
  historyBack = false,
}: {
  backTo: string;
  className?: string;
  historyBack?: boolean;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const target = getNavBackTarget(location.state, backTo);
  const useStackTransition = shouldUseSettingsStackTransition(location.pathname, target);
  const exitSettingsToApp = isExitingSettingsToApp(location.pathname, target);
  const exitSettingsSubpage = isExitingSettingsSubpage(location.pathname, target);

  return (
    <Link
      className={className}
      to={target}
      aria-label="Back"
      onClick={(event) => {
        if (historyBack && hasHistoryBackEntry(location.key)) {
          event.preventDefault();
          navigate(-1);
          return;
        }

        if (!useStackTransition) return;

        if (exitSettingsToApp || exitSettingsSubpage) {
          event.preventDefault();
          beginStackExitAnimation();
          markSkipStackEnter();
          window.setTimeout(() => {
            endStackExitAnimation();
            navigate(target);
          }, STACK_TRANSITION_MS);
          return;
        }

        markNavDirection("back");
      }}
    >
      <ArrowLeft size={20} />
    </Link>
  );
}

export function AppHeader({
  title,
  subtitle,
  backTo,
  historyBack = false,
  desktopHidden = false,
  mobileHidden = false,
  edgeToEdge = false,
  trailing,
}: {
  title: string;
  subtitle?: string;
  backTo?: string;
  historyBack?: boolean;
  desktopHidden?: boolean;
  mobileHidden?: boolean;
  edgeToEdge?: boolean;
  trailing?: ReactNode;
}) {
  return (
    <header
      className={[
        "screen-header",
        desktopHidden ? "desktop-hidden" : "",
        mobileHidden ? "mobile-hidden" : "",
        edgeToEdge ? "screen-header--edge-to-edge" : "",
      ].filter(Boolean).join(" ")}
    >
      {backTo ? (
        <BackLink backTo={backTo} className="icon-button" historyBack={historyBack} />
      ) : (
        <span className="screen-header-side-spacer" aria-hidden="true" />
      )}
      <div className="screen-header-copy">
        <div className="screen-header-title-row">
          <strong>{title}</strong>
          {subtitle ? <span className="screen-header-status">{subtitle}</span> : null}
        </div>
      </div>
      {trailing ?? <span className="screen-header-trailing-spacer" aria-hidden="true" />}
    </header>
  );
}
